/**
 * FunFactsService
 * 
 * Placeholder service for managing fun facts and curiosities about searched entities.
 * This service is designed to integrate with backend APIs when they become available.
 * 
 * @module services/funFactsService
 */

/**
 * Fetches fun facts for a given search query
 * 
 * TODO: Integrate with backend API endpoint when available
 * Expected backend endpoint: GET /api/entities/:entityId/fun-facts
 * 
 * @param {string} query - The search query
 * @param {string} entityId - Optional entity ID if matched
 * @returns {Promise<Array<string>>} Promise resolving to array of fun facts
 */
export const getFunFacts = async (query, entityId = null) => {
    // Placeholder implementation
    // When backend is ready, this will fetch from:
    // - Entity database (description, curiosities from RAG)
    // - Category-based facts
    // - General interest facts as fallback
    
    return new Promise((resolve) => {
        // Simulate async operation
        setTimeout(() => {
            resolve([]);
        }, 0);
    });
};

/**
 * Gets a random fun fact from the available facts
 * 
 * @param {Array<string>} facts - Array of fun facts
 * @returns {string|null} A random fun fact or null if no facts available
 */
export const getRandomFunFact = (facts) => {
    if (!facts || facts.length === 0) {
        return null;
    }
    
    const randomIndex = Math.floor(Math.random() * facts.length);
    return facts[randomIndex];
};

/**
 * Rotates through fun facts with a given interval
 * 
 * @param {Array<string>} facts - Array of fun facts
 * @param {number} intervalMs - Interval in milliseconds between rotations
 * @param {Function} callback - Callback function called with each fact
 * @returns {Function} Cleanup function to stop rotation
 */
export const rotateFunFacts = (facts, intervalMs = 5000, callback) => {
    if (!facts || facts.length === 0 || !callback) {
        return () => {};
    }

    let currentIndex = 0;
    
    // Show first fact immediately
    callback(facts[currentIndex]);
    
    const intervalId = setInterval(() => {
        currentIndex = (currentIndex + 1) % facts.length;
        callback(facts[currentIndex]);
    }, intervalMs);

    // Return cleanup function
    return () => {
        clearInterval(intervalId);
    };
};

/**
 * Placeholder for future backend integration
 * This will be called when the backend API is ready
 * 
 * @param {string} query - Search query
 * @param {Object} entityData - Entity data from backend (when available)
 * @returns {Array<string>} Extracted fun facts from entity data
 */
export const extractFactsFromEntity = (query, entityData) => {
    // TODO: Extract facts from entity data structure
    // Expected entity data structure:
    // {
    //   description: string,
    //   curiosities: string[],
    //   category: string,
    //   ...
    // }
    
    const facts = [];
    
    if (entityData?.description) {
        // Extract interesting snippets from description
        // This is a placeholder - actual implementation will parse entity data
    }
    
    if (entityData?.curiosities && Array.isArray(entityData.curiosities)) {
        facts.push(...entityData.curiosities);
    }
    
    return facts;
};













